import React, { useCallback, useState } from 'react';
import { Save, RefreshCw, Type, EyeIcon, FileText, Laptop, Bookmark } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import TextParametersSection from './TextParametersSection';
import FontParametersSection from './FontParametersSection';
import DeviceContextSection from './DeviceContextSection';
import RussianStandardsSection from './RussianStandardsSection';

// Импорт утилитарных функций
import { isPositiveNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета размера текста
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  fontFamilies,
  deviceTypes,
  russianStandards,
  targetAudiences,
  savePreset 
}) => {
  
  // Состояние для имени сохраняемого пресета
  const [savePresetName, setSavePresetName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем другие поля в зависимости от изменений
      if (name === 'deviceType') {
        const selectedDevice = deviceTypes.find(device => device.id === fieldValue);
        if (selectedDevice) {
          // Обновляем размер шрифта соответственно типу устройства
          newState.fontSize = selectedDevice.defaultFontSize.toString();
        }
      }

      if (name === 'targetAudience') {
        // Настраиваем размер шрифта в зависимости от целевой аудитории
        if (fieldValue === 'elderly' || fieldValue === 'visually-impaired') {
          newState.fontSize = (parseFloatOrDefault(prev.fontSize, 16) * 1.25).toString();
          newState.lineHeight = '1.6';
        } else if (fieldValue === 'dyslexic') {
          newState.lineHeight = '1.8';
          newState.letterSpacing = '0.05';
        } else if (fieldValue === 'children') {
          newState.fontSize = Math.max(parseFloatOrDefault(prev.fontSize, 16), 18).toString();
        }
      }

      // Сбрасываем настройки доступности если она отключена
      if (name === 'includeAccessibility' && !checked) {
        newState.fontScale = '100';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [deviceTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      fontSize, 
      fontWeight, 
      lineHeight, 
      letterSpacing, 
      viewingDistance,
      fontScale
    } = formData;

    // Парсинг числовых значений
    const numFontSize = parseFloatOrDefault(fontSize, -1);
    const numFontWeight = parseFloatOrDefault(fontWeight, -1);
    const numLineHeight = parseFloatOrDefault(lineHeight, -1);
    const numLetterSpacing = parseFloatOrDefault(letterSpacing, -999);
    const numViewingDistance = parseFloatOrDefault(viewingDistance, -1);
    const numFontScale = parseFloatOrDefault(fontScale, -1);

    // Валидация базовых полей
    if (numFontSize <= 0) newErrors.fontSize = 'Размер шрифта должен быть положительным.';
    if (!newErrors.fontSize && numFontSize > 500) newErrors.fontSize = 'Максимальный размер шрифта 500.';
    
    if (numFontWeight <= 0) newErrors.fontWeight = 'Насыщенность должна быть положительной.';
    if (!newErrors.fontWeight && numFontWeight > 900) newErrors.fontWeight = 'Насыщенность не может быть больше 900.';
    
    if (numLineHeight <= 0) newErrors.lineHeight = 'Высота строки должна быть положительной.';
    if (!newErrors.lineHeight && numLineHeight > 3) newErrors.lineHeight = 'Высота строки не может быть больше 3.';
    
    if (numLetterSpacing < -1) newErrors.letterSpacing = 'Минимальное межбуквенное расстояние -1.';
    if (!newErrors.letterSpacing && numLetterSpacing > 1) newErrors.letterSpacing = 'Максимальное межбуквенное расстояние 1.';
    
    if (numViewingDistance <= 0) newErrors.viewingDistance = 'Расстояние просмотра должно быть положительным.';
    if (!newErrors.viewingDistance && numViewingDistance > 500) newErrors.viewingDistance = 'Максимальное расстояние просмотра 500 см.';
    
    if (numFontScale <= 0) newErrors.fontScale = 'Масштаб шрифта должен быть положительным.';
    if (!newErrors.fontScale && numFontScale > 300) newErrors.fontScale = 'Максимальный масштаб шрифта 300%.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета
  const calculateTextSize = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const fontSize = parseFloatOrDefault(formData.fontSize);
        const fontSizeUnit = formData.fontSizeUnit;
        const fontFamily = formData.fontFamily;
        const fontWeight = parseFloatOrDefault(formData.fontWeight);
        const lineHeight = parseFloatOrDefault(formData.lineHeight);
        const letterSpacing = parseFloatOrDefault(formData.letterSpacing);
        const mediaType = formData.mediaType;
        const targetAudience = formData.targetAudience;
        const viewingDistance = parseFloatOrDefault(formData.viewingDistance);
        const colorScheme = formData.colorScheme;
        const fontScale = parseFloatOrDefault(formData.fontScale) / 100;
        const deviceType = formData.deviceType;
        const russianStandard = formData.russianStandard;
        const includeAccessibility = formData.includeAccessibility;

        // Получаем характеристики устройства
        const device = deviceTypes.find(d => d.id === deviceType) || deviceTypes[0];
        const deviceBaseFontSize = device.defaultFontSize;
        const deviceWidth = device.defaultWidth;

        // --- Расчеты размеров в различных единицах измерения ---
        
        // Базовый размер в пикселях
        let fontSizeInPx = fontSize;
        
        // Конвертируем исходный размер в пиксели, если он задан в других единицах
        if (fontSizeUnit === 'em') {
          fontSizeInPx = fontSize * deviceBaseFontSize;
        } else if (fontSizeUnit === 'rem') {
          fontSizeInPx = fontSize * deviceBaseFontSize;
        } else if (fontSizeUnit === 'pt') {
          fontSizeInPx = fontSize * (96 / 72); // 1pt = 1/72 дюйма, 1 дюйм = 96px на стандартном экране
        } else if (fontSizeUnit === 'px') {
          fontSizeInPx = fontSize;
        } else if (fontSizeUnit === '%') {
          fontSizeInPx = (fontSize / 100) * deviceBaseFontSize;
        } else if (fontSizeUnit === 'vw') {
          fontSizeInPx = (fontSize / 100) * deviceWidth;
        }
        
        // Применяем масштаб шрифта (настройки доступности)
        if (includeAccessibility) {
          fontSizeInPx = fontSizeInPx * fontScale;
        }
        
        // Конвертируем пиксели в различные единицы измерения
        const fontSizeInEm = fontSizeInPx / deviceBaseFontSize;
        const fontSizeInRem = fontSizeInPx / deviceBaseFontSize;
        const fontSizeInPt = fontSizeInPx * (72 / 96);
        const fontSizeInPercent = (fontSizeInPx / deviceBaseFontSize) * 100;
        const fontSizeInVw = (fontSizeInPx / deviceWidth) * 100;
        
        // --- Рассчитываем соответствие требованиям и стандартам ---
        
        // Оценка удобочитаемости (минимальный размер для разных возрастных групп)
        let readabilityScore = 100; // 100 - отлично, 0 - плохо
        
        // Уменьшаем оценку, если размер меньше рекомендуемого для данной аудитории
        if (targetAudience === 'elderly' && fontSizeInPx < 18) {
          readabilityScore -= (18 - fontSizeInPx) * 5;
        } else if (targetAudience === 'visually-impaired' && fontSizeInPx < 20) {
          readabilityScore -= (20 - fontSizeInPx) * 6;
        } else if (targetAudience === 'children' && fontSizeInPx < 16) {
          readabilityScore -= (16 - fontSizeInPx) * 5;
        } else if (fontSizeInPx < 14) {
          readabilityScore -= (14 - fontSizeInPx) * 5;
        }
        
        // Учитываем расстояние просмотра (для веб и электронных изданий)
        // Минимальный размер текста в зависимости от расстояния просмотра
        // Формула на основе принципа: угловой размер не менее 15 угловых минут
        const minFontSizeByDistance = Math.round(viewingDistance * 0.27);
        if (fontSizeInPx < minFontSizeByDistance) {
          readabilityScore -= (minFontSizeByDistance - fontSizeInPx) * 3;
        }
        
        // Проверка на соответствие российским стандартам
        const gostCompliance = {};
        
        // ГОСТ Р 54723-2019 (Интернет-ресурсы и электронные издания)
        if (russianStandard === 'GOST-R-54723' || russianStandard === '') {
          const minFontSize = (mediaType === 'print') ? 10 : 12;
          gostCompliance['GOST-R-54723'] = {
            compliant: fontSizeInPx >= minFontSize,
            minRequired: minFontSize,
            recommendation: fontSizeInPx < minFontSize 
              ? `Увеличьте размер шрифта до ${minFontSize}px согласно ГОСТ Р 54723-2019`
              : ''
          };
        }
        
        // ГОСТ Р 58511-2019 (Издания. Требования к шрифтовому оформлению)
        if (russianStandard === 'GOST-R-58511' || russianStandard === '') {
          let minFontSize = 12;
          if (targetAudience === 'children') {
            minFontSize = 16;
          } else if (mediaType === 'print') {
            minFontSize = 9;
          }
          
          gostCompliance['GOST-R-58511'] = {
            compliant: fontSizeInPx >= minFontSize,
            minRequired: minFontSize,
            recommendation: fontSizeInPx < minFontSize 
              ? `Увеличьте размер шрифта до ${minFontSize}px согласно ГОСТ Р 58511-2019`
              : ''
          };
        }
        
        // ГОСТ Р 57850-2017 (Оформление сайтов для слабовидящих)
        if ((russianStandard === 'GOST-P-57850' || russianStandard === '') && (targetAudience === 'visually-impaired' || includeAccessibility)) {
          const minFontSize = 18;
          const requiredLineHeight = 1.5;
          const isLineHeightCompliant = lineHeight >= requiredLineHeight;
          
          gostCompliance['GOST-P-57850'] = {
            compliant: fontSizeInPx >= minFontSize && isLineHeightCompliant,
            minRequired: minFontSize,
            recommendedLineHeight: requiredLineHeight,
            recommendation: !isLineHeightCompliant 
              ? `Увеличьте межстрочный интервал до ${requiredLineHeight} согласно ГОСТ Р 57850-2017`
              : fontSizeInPx < minFontSize 
                ? `Увеличьте размер шрифта до ${minFontSize}px согласно ГОСТ Р 57850-2017`
                : ''
          };
        }
        
        // СанПиН 1.2.3685-21 (Гигиенические нормативы для электронных изданий)
        if (russianStandard === 'SanPin' || russianStandard === '') {
          let minFontSize = 12;
          if (targetAudience === 'children') {
            minFontSize = 14;
          } else if (targetAudience === 'elderly' || targetAudience === 'visually-impaired') {
            minFontSize = 16;
          }
          
          gostCompliance['SanPin'] = {
            compliant: fontSizeInPx >= minFontSize,
            minRequired: minFontSize,
            recommendation: fontSizeInPx < minFontSize 
              ? `Увеличьте размер шрифта до ${minFontSize}px согласно СанПиН 1.2.3685-21`
              : ''
          };
        }
        
        // Определение общего соответствия стандартам
        const standardsCompliance = Object.values(gostCompliance).every(standard => standard.compliant);
        
        // Рекомендации по улучшению
        const improvements = [];
        
        // Добавляем рекомендации из ГОСТ
        Object.values(gostCompliance).forEach(standard => {
          if (standard.recommendation) {
            improvements.push(standard.recommendation);
          }
        });
        
        // Рекомендации по улучшению удобочитаемости
        if (readabilityScore < 80) {
          if (lineHeight < 1.5) {
            improvements.push(`Увеличьте межстрочное расстояние до 1.5-1.8 для лучшей удобочитаемости.`);
          }
          
          if (fontSizeInPx < 16 && mediaType === 'screen') {
            improvements.push(`Рекомендуемый размер шрифта для чтения с экрана минимум 16px.`);
          }
          
          if (colorScheme === 'dark' && parseFloat(fontWeight) < 500) {
            improvements.push(`Используйте более жирное начертание (500+) для темного фона.`);
          }
        }
        
        // Рассчитываем контрольные точки адаптивности
        const responsiveBreakpoints = {
          small: {
            fontSize: `${fontSizeInPx * 0.85}px`,
            lineHeight: lineHeight,
            recommendation: fontSizeInPx * 0.85 < 12 ? 'Размер шрифта на малых экранах слишком мал' : ''
          },
          medium: {
            fontSize: `${fontSizeInPx}px`,
            lineHeight: lineHeight,
            recommendation: ''
          },
          large: {
            fontSize: `${fontSizeInPx * 1.15}px`,
            lineHeight: Math.min(lineHeight * 1.1, 2),
            recommendation: ''
          }
        };
        
        // Рассчитываем CSS для применения
        const cssCode = `font-size: ${fontSizeInPx}px; /* ${fontSizeInRem.toFixed(3)}rem */
font-family: ${fontFamily};
font-weight: ${fontWeight};
line-height: ${lineHeight};
letter-spacing: ${letterSpacing}em;
text-align: ${formData.textAlign};
color: ${colorScheme === 'dark' ? '#ffffff' : '#333333'};
background-color: ${colorScheme === 'dark' ? '#333333' : '#ffffff'};`;

        const responsiveCss = `/* Desktop */
@media screen and (min-width: 992px) {
  .responsive-text {
    font-size: ${responsiveBreakpoints.large.fontSize};
    line-height: ${responsiveBreakpoints.large.lineHeight};
  }
}

/* Tablet */
@media screen and (min-width: 768px) and (max-width: 991px) {
  .responsive-text {
    font-size: ${responsiveBreakpoints.medium.fontSize};
    line-height: ${responsiveBreakpoints.medium.lineHeight};
  }
}

/* Mobile */
@media screen and (max-width: 767px) {
  .responsive-text {
    font-size: ${responsiveBreakpoints.small.fontSize};
    line-height: ${responsiveBreakpoints.small.lineHeight};
  }
}`;
        
        // --- Готовим итоговые результаты ---
        const finalResults = {
          fontSizes: {
            px: fontSizeInPx.toFixed(2),
            em: fontSizeInEm.toFixed(3),
            rem: fontSizeInRem.toFixed(3),
            pt: fontSizeInPt.toFixed(2),
            percent: fontSizeInPercent.toFixed(1),
            vw: fontSizeInVw.toFixed(3),
          },
          readability: {
            score: Math.max(0, Math.min(100, Math.round(readabilityScore))),
            minSizeForDistance: minFontSizeByDistance,
            viewingDistance: viewingDistance
          },
          standards: {
            gostCompliance,
            standardsCompliance,
            improvements
          },
          responsiveBreakpoints,
          cssCode,
          responsiveCss,
          mediaType,
          targetAudience
        };

        setResults(finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, deviceTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      textContent: 'Пример текста для проверки размера и читабельности. Эта строка поможет оценить, как выглядит ваш текст с выбранными параметрами шрифта в различных контекстах.',
      fontSize: '16',
      fontSizeUnit: 'px',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: '400',
      lineHeight: '1.5',
      letterSpacing: '0',
      textAlign: 'left',
      mediaType: 'screen',
      targetAudience: 'general',
      viewingDistance: '50',
      colorScheme: 'light',
      fontScale: '100',
      deviceType: 'desktop',
      russianStandard: 'GOST-R-54723',
      includeAccessibility: true
    });
    setResults(null);
    setErrors({});
    setSavePresetName('');
    setShowSaveDialog(false);
  };

  // Обработчик сохранения пресета
  const handleSavePreset = () => {
    if (showSaveDialog) {
      const savedResult = savePreset(savePresetName || null);
      if (savedResult) {
        alert(`Пресет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSavePresetName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateTextSize();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Type className="w-6 h-6 text-indigo-500" />
          Параметры текста
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Секция параметров текста */}
        <TextParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Секция параметров шрифта */}
        <FontParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          fontFamilies={fontFamilies}
        />

        {/* Секция контекста устройства */}
        <DeviceContextSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          deviceTypes={deviceTypes}
          targetAudiences={targetAudiences}
        />

        {/* Секция российских стандартов */}
        <RussianStandardsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          russianStandards={russianStandards}
        />

        {/* Диалог сохранения пресета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={savePresetName}
                onChange={(e) => setSavePresetName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSavePreset}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<EyeIcon className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать и просмотреть'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSavePreset}
              icon={<Bookmark className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить пресет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;